import React from "react";

// ─── Deployment Validation Panel (V3) ────────────────────────────────
// Renders a `deployment_validation_pending` response. In V3 nothing is held on the
// server: the user answers here, and the parent RESENDS THE SAME deploy request with
// the answers added. This panel only collects the answers and hands them up via
// onAnswers({ pipelines, pipelineUploads, files, configMerges, skip }); the parent
// merges them into the original request and re-posts the deploy endpoint.
//
// missingItems (V3): { kind:"pipeline"|"credential", service, field?, expectedPath?,
//   platform?, canSkip, message, options:[{value,label}] }.
//
// Props: data, onAnswers(answers), disabled

const C = {
  bg: "#0e1219", panel: "#121825", border: "rgba(255,255,255,0.08)",
  accent: "#3b7fff", text: "#dce8ff", sub: "#aac0dc", muted: "#7a93bc",
  good: "#00c98d", warn: "#f5a623", bad: "#ff6b6b",
};
const input = { width: "100%", background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 9px", fontSize: 13, outline: "none", boxSizing: "border-box" };

export default function DeploymentValidationPanel({ data, onAnswers, disabled }) {
  const items = (data && data.missingItems) || [];
  // Per-item answer state keyed by a stable item key.
  const [ans, setAns] = React.useState({});
  const keyOf = (it, i) => `${it.kind}:${it.service || ""}:${it.field || it.expectedPath || i}`;
  const set = (k, patch) => setAns((p) => ({ ...p, [k]: { ...(p[k] || {}), ...patch } }));

  const buildAndSubmit = () => {
    const pipelines = [];
    const pipelineUploads = [];
    const files = {};
    const configMerges = []; // { path, value } merged into config/deploymentConfig
    const skip = [];
    let pipeIdx = 0;
    let ok = true;

    items.forEach((it, i) => {
      const k = keyOf(it, i);
      const a = ans[k] || {};
      if (it.kind === "pipeline") {
        const choice = a.choice || (it.canSkip ? "" : "default");
        if (choice === "default") pipelines.push({ service: it.service, choice: "default" });
        else if (choice === "upload") {
          if (!a.file) { ok = false; return; }
          pipeIdx += 1;
          const field = `pipeline_${pipeIdx}`;
          pipelineUploads.push({ service: it.service, field });
          files[field] = a.file;
        } else if (choice === "skip" && it.canSkip) {
          if (it.field) skip.push(it.field);
        } else { ok = false; }
      } else if (it.kind === "credential") {
        if (a.choice === "skip" && it.canSkip) { if (it.field) skip.push(it.field); return; }
        if (it.field === "keyVault.servicePrincipal") {
          // Azure Pipelines (spring/.NET): service principal to create the service connection.
          if (!a.tenantId || !a.subscriptionId || !a.clientId || !a.clientSecret) { ok = false; return; }
          configMerges.push({ path: "keyVault.servicePrincipal", value: { tenantId: a.tenantId, subscriptionId: a.subscriptionId, clientId: a.clientId, clientSecret: a.clientSecret } });
        } else if (it.field === "keyVault.connectionName") {
          if (!a.connectionName) { ok = false; return; }
          configMerges.push({ path: "keyVault.connectionName", value: a.connectionName });
        } else {
          // Nexus / EE repository login (anypoint.nexusCredentials).
          if (!a.username || !a.password) { ok = false; return; }
          configMerges.push({ path: it.field || "anypoint.nexusCredentials", value: { username: a.username, password: a.password } });
        }
      }
    });

    if (!ok) return;
    onAnswers && onAnswers({ pipelines, pipelineUploads, files, configMerges, skip });
  };

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: C.warn, marginBottom: 6 }}>⚠ A few things are needed before deploying</div>
      {data && data.message && <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 10 }}>{data.message}</div>}
      {(data && data.warnings || []).map((w, i) => <div key={i} style={{ fontSize: 11.5, color: C.warn, marginBottom: 4 }}>• {w}</div>)}

      {items.map((it, i) => {
        const k = keyOf(it, i);
        const a = ans[k] || {};
        const opts = it.options || (it.kind === "pipeline"
          ? [{ value: "default", label: "Use the Intelog default workflow" }, { value: "upload", label: "Upload your own workflow" }]
          : []);
        return (
          <div key={k} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12.5, color: C.text, fontWeight: 600, marginBottom: 2 }}>
              {it.service ? <span style={{ fontFamily: "monospace", color: C.accent }}>{it.service}</span> : null} {it.message}
            </div>
            {it.expectedPath && <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 6 }}>{it.expectedPath}</div>}

            {it.kind === "pipeline" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {opts.map((o) => (
                  <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.sub }}>
                    <input type="radio" name={k} checked={a.choice === o.value} onChange={() => set(k, { choice: o.value })} /> {o.label}
                  </label>
                ))}
                {it.canSkip && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.sub }}>
                    <input type="radio" name={k} checked={a.choice === "skip"} onChange={() => set(k, { choice: "skip" })} /> Skip
                  </label>
                )}
                {a.choice === "upload" && (
                  <input type="file" style={{ fontSize: 12, color: C.sub }} onChange={(e) => set(k, { file: e.target.files && e.target.files[0] })} />
                )}
              </div>
            )}

            {it.kind === "credential" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {it.field === "keyVault.servicePrincipal" ? (
                  <>
                    <input style={input} placeholder="tenantId" value={a.tenantId || ""} onChange={(e) => set(k, { tenantId: e.target.value })} />
                    <input style={input} placeholder="subscriptionId" value={a.subscriptionId || ""} onChange={(e) => set(k, { subscriptionId: e.target.value })} />
                    <input style={input} placeholder="clientId" value={a.clientId || ""} onChange={(e) => set(k, { clientId: e.target.value })} />
                    <input style={input} type="password" placeholder="clientSecret" value={a.clientSecret || ""} onChange={(e) => set(k, { clientSecret: e.target.value })} />
                  </>
                ) : it.field === "keyVault.connectionName" ? (
                  <input style={input} placeholder="service connection name" value={a.connectionName || ""} onChange={(e) => set(k, { connectionName: e.target.value })} />
                ) : (
                  <>
                    <input style={input} placeholder="username" value={a.username || ""} onChange={(e) => set(k, { username: e.target.value })} />
                    <input style={input} type="password" placeholder="password" value={a.password || ""} onChange={(e) => set(k, { password: e.target.value })} />
                  </>
                )}
                {it.canSkip && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.muted }}>
                    <input type="radio" name={k} checked={a.choice === "skip"} onChange={() => set(k, { choice: "skip" })} /> Skip this
                  </label>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button onClick={buildAndSubmit} disabled={disabled} style={{ padding: "8px 18px", borderRadius: 7, background: C.accent, border: "none", color: "#fff", fontWeight: 700, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>
        {disabled ? "Submitting…" : "Continue deployment"}
      </button>
    </div>
  );
}
